import React from 'react';
import { Link } from 'react-router-dom';
import {
  ClipboardDocumentListIcon,
  BellAlertIcon,
  ChartBarIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import LandlordToolkitLayout from '../../components/landlord-toolkit/LandlordToolkitLayout';
import BookDemoCard from '../../components/landlord-toolkit/BookDemoCard';

const FeatureItem: React.FC<{
  title: string;
  description: string;
  icon: React.ElementType;
  route?: string;
  badge?: string;
}> = ({ title, description, icon: Icon, route, badge }) => (
  <div className="group bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
    <div className="flex items-start justify-between mb-3">
      <div className="p-3 rounded-xl bg-blue-50">
        <Icon className="w-6 h-6 text-blue-600" />
      </div>
      {badge && (
        <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded-full">
          {badge}
        </span>
      )}
    </div>
    <h3 className="text-lg font-semibold text-gray-900 mb-1">{title}</h3>
    <p className="text-sm text-gray-600 mb-3">{description}</p>
    {route && (
      <Link
        to={route}
        className="inline-flex items-center text-blue-600 font-medium text-sm hover:text-blue-700"
      >
        Open Tool
      </Link>
    )}
  </div>
);

const LandlordToolkitDocs: React.FC = () => {
  return (
    <LandlordToolkitLayout title="Documentation">
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
        <section className="bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 text-white py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                Landlord Toolkit Documentation
              </h1>
              <p className="text-lg text-blue-100">
                Learn about the available tools to manage your properties, track rent, and
                streamline tenant communications.
              </p>
            </div>
          </div>
        </section>

        <section className="py-10">
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Overview</h2>
              <p className="text-gray-700 text-sm leading-relaxed">
                The Landlord Toolkit groups essential tools for private property management. It
                focuses on organizing your portfolio, tracking rent status, sending reminders, and
                accessing calculators and reports designed for landlords.
              </p>
            </div>

            <div className="mt-10">
              <BookDemoCard />
            </div>

            <div className="mt-8 grid gap-6 sm:grid-cols-2">
              <FeatureItem
                title="Rent Payment Tracker"
                description="Track all rent payments across your properties. View payment status, send reminders, and manage tenancies from one dashboard."
                icon={ClipboardDocumentListIcon}
                route="/property-tools/rent-tracker"
                badge="Most Popular"
              />
              <FeatureItem
                title="Automated Reminders"
                description="Control which automated reminders RentSmart sends to your tenants and turn all reminders off if needed."
                icon={BellAlertIcon}
                route="/landlord-toolkit/reminders"
              />
              <FeatureItem
                title="Rent Review Calculator"
                description="Calculate allowable rent changes using RTB HICP logic. Uses the most recently available HICP for compliance."
                icon={ChartBarIcon}
                route="/property-tools/rent-review-calculator"
                badge="New"
              />
              <FeatureItem
                title="Maintenance Requests"
                description="View and manage maintenance requests from your tenants."
                icon={WrenchScrewdriverIcon}
                route="/landlord-dashboard/maintenance"
              />
            </div>

            <div className="mt-10 bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Getting Started</h2>
              <ol className="list-decimal ml-5 space-y-2 text-sm text-gray-700">
                <li>Open the Landlord Toolkit from your dashboard.</li>
                <li>Use Quick Actions to add a managed property or view your portfolio.</li>
                <li>Configure Automated Reminders for your tenancies.</li>
                <li>Track payments and tenant status in Rent Payment Tracker.</li>
                <li>Use Rent Review Calculator to ensure RPZ compliance.</li>
              </ol>
            </div>

            <div className="mt-10 bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-3">FAQs</h2>
              <div className="space-y-3 text-sm text-gray-700">
                <p>
                  <span className="font-semibold">Is my managed property public?</span> No. Managed
                  properties are private and only visible in your toolkit.
                </p>
                <p>
                  <span className="font-semibold">Can I archive a property?</span> Yes. You can
                  archive/unarchive via the Managed Properties portfolio.
                </p>
                <p>
                  <span className="font-semibold">How do reminders work?</span> You can enable or
                  disable automated reminders per tenancy in the Reminders tool.
                </p>
              </div>
            </div>

            <div className="mt-10">
              <BookDemoCard />
            </div>

            <div className="mt-10 text-center">
              <Link
                to="/landlord-toolkit"
                className="inline-flex items-center px-6 py-3 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700"
              >
                Back to Landlord Toolkit
              </Link>
            </div>
          </div>
        </section>
      </div>
    </LandlordToolkitLayout>
  );
};

export default LandlordToolkitDocs;
